import { api } from './api.js';
import { ping, ring, stopRing } from './ring.js';

/**
 * Push registration for the native app.
 *
 * Only runs inside the Capacitor webview — browser web-push needs a service
 * worker and VAPID keys, which are a separate setup, so on the web this is a
 * no-op. Everything is dynamically imported and guarded so a missing plugin or
 * a denied permission never breaks sign-in.
 */

function isNative() {
  return typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;
}

// Push is only safe to initialise when Firebase is actually configured for the
// Android build (a google-services.json is present). Without it, calling
// PushNotifications.register() reaches FirebaseMessaging with no initialised
// FirebaseApp and crashes the app *natively* — which a JS try/catch cannot
// stop. So registration is gated behind a build flag set only when the app is
// built with Firebase in place: `VITE_PUSH_ENABLED=1`. Default off = never
// touch the native FCM path = never crash.
const PUSH_ENABLED = import.meta.env.VITE_PUSH_ENABLED === '1';

// The loud channel for new orders and delivery offers. Only the driver and
// admin apps ship its sound (res/raw/order_ring.mp3). The id must match the
// API (notify.js RING_CHANNEL); Android fixes a channel's sound on creation,
// so a new sound needs a new id.
const RING_CHANNEL = 'order_ring_v2';
const HAS_RING_CHANNEL = ['driver', 'admin'].includes(import.meta.env.VITE_APP_TARGET);

/** The native alert-channel plugin, on the builds that ship it. */
async function ringChannel() {
  if (!isNative()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const plugin = registerPlugin('RingChannel');
    // Older installs predate the plugin; treat a missing implementation as absent.
    await plugin.status();
    return plugin;
  } catch {
    return null;
  }
}

/** Whether alerts may interrupt Do Not Disturb, and how to ask for it. */
export async function dndStatus() {
  const plugin = await ringChannel();
  if (!plugin) return null;
  try {
    return await plugin.status();
  } catch {
    return null;
  }
}

export async function requestDndAccess() {
  const plugin = await ringChannel();
  if (!plugin) return false;
  try {
    const res = await plugin.requestDndAccess();
    return Boolean(res?.opened);
  } catch {
    return false;
  }
}

let listenersAdded = false;
let registeredOnce = false;

export async function registerPush() {
  if (!PUSH_ENABLED || !isNative() || registeredOnce) return;
  registeredOnce = true;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    if (!listenersAdded) {
      listenersAdded = true;
      // The FCM/APNs token arrives asynchronously after register(); ship it to
      // the API so the notification service can target this device.
      PushNotifications.addListener('registration', async (token) => {
        const platform = window.Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android';
        try {
          await api.post('/api/me/device-token', { token: token.value, platform });
        } catch (err) {
          console.warn('[push] could not save device token', err?.message);
        }
      });
      PushNotifications.addListener('registrationError', (err) => {
        console.warn('[push] registration error', err);
      });
      // A notification arrived while the app is open → refresh the inbox/bell.
      // Android shows nothing in the tray while the app is open, so ring here.
      PushNotifications.addListener('pushNotificationReceived', (n) => {
        const d = n?.data ?? {};
        // Another partner took it, or the office did: stop ringing now rather
        // than at the next poll, and let the offer card refresh away. Silent —
        // this is housekeeping, not something to chime about.
        if (d.kind === 'offer_withdrawn') {
          if (d.offerId) stopRing(`offer:${d.offerId}`);
          window.dispatchEvent(new CustomEvent('hfb:notification', { detail: { kind: d.kind } }));
          return;
        }
        if (d.ring === '1') ring(d.offerId ? `offer:${d.offerId}` : d.orderId ? `order:${d.orderId}` : null);
        // Android shows no tray notification while the app is open, so an
        // ordinary alert would otherwise arrive in silence.
        else ping(d.offerId ?? d.orderId ?? d.paymentId ?? String(Date.now()));
        window.dispatchEvent(new CustomEvent('hfb:notification', { detail: { kind: d.kind } }));
      });
      // Tapped from the tray → open the screen it's about (App listens and routes).
      PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        const link = action?.notification?.data?.link;
        window.dispatchEvent(new CustomEvent('hfb:notification', { detail: { link } }));
      });
    }

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      registeredOnce = false; // let a later attempt re-prompt if the user changes their mind
      return;
    }
    if (HAS_RING_CHANNEL) {
      // Built natively so the ring sounds on the ALARM stream (silent mode
      // doesn't mute it) and can pass through Do Not Disturb. Capacitor's own
      // createChannel can do neither, and is the fallback only.
      const native = await ringChannel();
      if (native) {
        await native.ensure().catch((e) => console.warn('[push] ring channel', e?.message));
      } else {
        await PushNotifications.createChannel({
          id: RING_CHANNEL,
          name: 'New orders & offers',
          description: 'A loud ring for each new order or delivery offer',
          importance: 5,
          visibility: 1,
          sound: 'order_ring.mp3',
          vibration: true,
          lights: true,
          lightColor: '#1B8A45',
        }).catch((e) => console.warn('[push] ring channel', e?.message));
      }
    }
    await PushNotifications.register();
  } catch (err) {
    registeredOnce = false;
    console.warn('[push] unavailable', err?.message);
  }
}
